/** SUNTRONIX ONE — Global IT Hardware Visual Catalogue (Phase 1). */
import { A11yDrawer } from "@/components/app/A11yDrawer";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Laptop, Monitor, Server, HardDrive, Network, Printer, Smartphone,
  Battery, Headphones, Cable, Apple, ChevronRight, ChevronLeft, Search,
  Package, Home, ShoppingCart, Trash2, X, Send, FileText, Layers, Plus,
  MoreVertical, Pencil, Power, History as HistoryIcon, FilePlus, Upload,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { HsnPicker } from "@/components/app/HsnPicker";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const ICON = {
  Laptop, Monitor, Server, HardDrive, Network, Printer, Smartphone,
  Battery, Headphones, Cable, Apple,
};

const fmtINR = (n) =>
  n ? new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n)
    : "Price on request";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const brandLogoSrc = (br) => {
  const u = br?.logo_url;
  if (!u) return null;
  return u.startsWith("/") ? `${BACKEND}${u}` : u;
};

// Controlled-open helper: lets Radix transition to CLOSED (restoring
// body pointer-events) BEFORE the component unmounts — avoids the
// intermittent page-freeze from force-unmounting an always-open dialog.
function useManagedDialog() {
  const [open, setOpen] = useState(true);
  const closeThen = useCallback((cb) => { setOpen(false); setTimeout(() => cb && cb(), 180); }, []);
  return { open, closeThen };
}

function ConfirmDialog({ title, body, confirmLabel, onConfirm, onClose, testid, confirmTestid, cancelTestid, destructive = true }) {
  const { open, closeThen } = useManagedDialog();
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeThen(onClose); }}>
      <DialogContent data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <p className="text-[13px] text-slate-600">{body}</p>
        <DialogFooter>
          <Button variant="outline" onClick={() => closeThen(onClose)} data-testid={cancelTestid}>Cancel</Button>
          <Button className={destructive ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-slate-900 text-white"}
            onClick={() => closeThen(onConfirm)} data-testid={confirmTestid}>{confirmLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BlockedDialog({ title, message, actions, onClose, testid }) {
  const { open, closeThen } = useManagedDialog();
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeThen(onClose); }}>
      <DialogContent data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <p className="text-[13px] text-slate-600">{message}</p>
        <DialogFooter className="gap-2">
          {(actions || []).map((a, i) => (
            <Button key={i} variant={a.variant || "outline"}
              className={a.primary ? "bg-slate-900 text-white" : ""}
              onClick={() => closeThen(a.onClick)} data-testid={a.testid}>{a.label}</Button>
          ))}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function CatalogueV2() {
  const navigate = useNavigate();
  const [level, setLevel] = useState(1);        // 1 = categories, 2 = brands, 3 = products
  const [category, setCategory] = useState(null);
  const [brand, setBrand] = useState(null);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [productSel, setProductSel] = useState(null);
  const [collectFor, setCollectFor] = useState(null);   // product being added to a collection
  const [cart, setCart] = useState({ items: [], counts: { lines: 0, units: 0 }, subtotal_inr: 0 });
  const [cartOpen, setCartOpen] = useState(false);
  const { user } = useAuth();
  const isAdmin = ["super_admin", "management"].includes(user?.role) && !user?.master_company_id;

  const addToQuoteSingle = useCallback(async (product) => {
    try {
      const { data } = await api.post("/catalogue-collections/quote-lines",
        { product_codes: [product.code] });
      if (!data.items?.length) { toast.error("Product not quotable"); return; }
      sessionStorage.setItem("st:quote-prefill",
        JSON.stringify({ items: data.items, subject: product.name || "" }));
      navigate("/commercial/crm/quotations?new=1&prefill=1");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not build quote"); }
  }, [navigate]);


  const loadCart = useCallback(async () => {
    try {
      const { data } = await api.get("/catalogue-cart");
      setCart({
        items: data.cart?.items || [],
        counts: data.counts || { lines: 0, units: 0 },
        subtotal_inr: data.subtotal_inr || 0,
      });
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadCart(); }, [loadCart]);

  const addToCart = useCallback(async (product, qty = 1) => {
    try {
      const { data } = await api.post("/catalogue-cart/items", {
        product_code: product.code, quantity: qty,
      });
      setCart({
        items: data.cart?.items || [],
        counts: data.counts || { lines: 0, units: 0 },
        subtotal_inr: data.subtotal_inr || 0,
      });
      toast.success(`Added ${product.name} to cart`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add to cart");
    }
  }, []);

  const removeFromCart = useCallback(async (product_code) => {
    try {
      const { data } = await api.delete(`/catalogue-cart/items/${product_code}`);
      setCart({
        items: data.cart?.items || [],
        counts: data.counts || { lines: 0, units: 0 },
        subtotal_inr: data.subtotal_inr || 0,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Remove failed");
    }
  }, []);

  const updateQty = useCallback(async (product_code, quantity) => {
    if (quantity < 1) return removeFromCart(product_code);
    try {
      const { data } = await api.patch(`/catalogue-cart/items/${product_code}`, {
        product_code, quantity,
      });
      setCart({
        items: data.cart?.items || [],
        counts: data.counts || { lines: 0, units: 0 },
        subtotal_inr: data.subtotal_inr || 0,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  }, [removeFromCart]);

  const requestQuote = useCallback(async (payload = {}) => {
    try {
      const { data } = await api.post("/catalogue-cart/request-quote", payload);
      toast.success(`Quote ${data.quote?.code} submitted`);
      setCart({ items: [], counts: { lines: 0, units: 0 }, subtotal_inr: 0 });
      setCartOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Quote submit failed");
    }
  }, []);

  const loadCategories = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/catalogue-v2/categories", { params: { include_inactive: isAdmin } });
      setCategories(data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  }, [isAdmin]);

  const [editCat, setEditCat] = useState(null);
  const [addCat, setAddCat] = useState(false);
  const [histCat, setHistCat] = useState(null);
  const [blockCat, setBlockCat] = useState(null);
  const deactivateCat = async (cat) => {
    try { const { data } = await api.post(`/catalogue-v2/categories/${cat.code}/deactivate`);
      toast.success(`Category ${data.status === "ACTIVE" ? "activated" : "deactivated"}`); loadCategories(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const [confirmDelCat, setConfirmDelCat] = useState(null);
  const deleteCat = async (cat) => {
    try { await api.delete(`/catalogue-v2/categories/${cat.code}`);
      toast.success("Category deleted"); setConfirmDelCat(null); loadCategories(); }
    catch (e) {
      const d = e.response?.data?._rawDetail ?? e.response?.data?.detail;
      if (d && typeof d === "object") { setConfirmDelCat(null); setBlockCat({ cat, ...d }); }
      else toast.error(d || "Delete failed");
    }
  };

  const loadBrands = useCallback(async (cat) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/catalogue-v2/categories/${cat.code}/brands`,
        { params: { include_empty: isAdmin, include_inactive: isAdmin } });
      setBrands(data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  }, [isAdmin]);

  const loadProducts = useCallback(async (cat, br, query = "") => {
    setBusy(true);
    try {
      const { data } = await api.get("/catalogue-v2/products",
        { params: { category_code: cat.code, brand_code: br?.code, q: query || undefined, include_inactive: isAdmin } });
      setProducts(data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  }, [isAdmin]);

  // ---- Brand actions (L2) ----
  const [addBrandFor, setAddBrandFor] = useState(null);
  const [logoBackfill, setLogoBackfill] = useState(false);
  const [editBrand, setEditBrand] = useState(null);
  const [histBrand, setHistBrand] = useState(null);
  const [blockBrand, setBlockBrand] = useState(null);
  const [confirmDelBrand, setConfirmDelBrand] = useState(null);
  const deactivateBrand = async (br) => {
    try { const { data } = await api.post(`/catalogue-v2/brands/${br.code}/deactivate`);
      toast.success(`Brand ${data.status === "ACTIVE" ? "activated" : "deactivated"}`); loadBrands(category); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const removeBrandFromCat = async (br) => {
    try { await api.post(`/catalogue-v2/categories/${category.code}/brands/${br.code}/remove`);
      toast.success(`Removed ${br.name} from ${category.name}`); loadBrands(category); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteBrand = async (br) => {
    try { await api.delete(`/catalogue-v2/brands/${br.code}`);
      toast.success("Brand deleted"); setConfirmDelBrand(null); loadBrands(category); }
    catch (e) {
      const d = e.response?.data?._rawDetail ?? e.response?.data?.detail;
      if (d && typeof d === "object") { setConfirmDelBrand(null); setBlockBrand({ br, ...d }); }
      else toast.error(d || "Delete failed");
    }
  };

  // ---- Model / product actions (L3) ----
  const [addModel, setAddModel] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editModel, setEditModel] = useState(null);
  const [dupModel, setDupModel] = useState(null);
  const [histModel, setHistModel] = useState(null);
  const [blockModel, setBlockModel] = useState(null);
  const [confirmDelModel, setConfirmDelModel] = useState(null);
  const refreshProducts = () => loadProducts(category, brand, q);
  const deactivateModel = async (p) => {
    try { const { data } = await api.post(`/catalogue-v2/products/${p.code}/deactivate`);
      toast.success(`Model ${data.status === "ACTIVE" ? "activated" : "deactivated"}`); refreshProducts(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteModel = async (p) => {
    try { await api.delete(`/catalogue-v2/products/${p.code}`);
      toast.success("Model deleted"); setConfirmDelModel(null); refreshProducts(); }
    catch (e) {
      const d = e.response?.data?._rawDetail ?? e.response?.data?.detail;
      if (d && typeof d === "object") { setConfirmDelModel(null); setBlockModel({ p, ...d }); }
      else toast.error(d || "Delete failed");
    }
  };

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const goCategory = (cat) => {
    setCategory(cat); setBrand(null); setLevel(2); loadBrands(cat);
  };
  const goBrand = (br) => {
    setBrand(br); setLevel(3); loadProducts(category, br, "");
  };
  const goHome = () => { setLevel(1); setCategory(null); setBrand(null); setProductSel(null); };
  const goBack = () => {
    if (level === 3) { setLevel(2); setBrand(null); setProductSel(null); }
    else if (level === 2) { goHome(); }
  };

  useEffect(() => {
    if (level !== 3 || !category) return;
    const t = setTimeout(() => loadProducts(category, brand, q), 200);
    return () => clearTimeout(t);
  }, [q, level, category, brand, loadProducts]);

  return (
    <div className="space-y-6 max-w-[1600px]" data-testid="catalogue-v2-page">
      {/* Header + breadcrumb */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">
            Technology · Global Hardware Catalogue
          </div>
          <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1 flex items-center gap-2">
            <Package className="h-6 w-6 text-slate-700" strokeWidth={1.6} />
            IT Hardware Catalogue
          </h1>
          <div className="text-[12px] text-slate-500 mt-2 flex items-center gap-1.5" data-testid="catalogue-breadcrumb">
            <button onClick={goHome} className="hover:text-slate-900 inline-flex items-center gap-1">
              <Home className="h-3 w-3" /> All Categories
            </button>
            {category && (
              <>
                <ChevronRight className="h-3 w-3 text-slate-400" />
                <button onClick={() => { setLevel(2); setBrand(null); setProductSel(null); }}
                  className="hover:text-slate-900">
                  {category.name}
                </button>
              </>
            )}
            {brand && (
              <>
                <ChevronRight className="h-3 w-3 text-slate-400" />
                <span className="text-slate-800 font-medium">{brand.name}</span>
              </>
            )}
          </div>
        </div>
        {level > 1 && (
          <Button variant="outline" onClick={goBack} className="h-9" data-testid="catalogue-back">
            <ChevronLeft className="h-4 w-4 mr-1.5" /> Back
          </Button>
        )}
        <Button
          onClick={() => setCartOpen(true)}
          className="h-9 relative"
          data-testid="catalogue-open-cart"
          variant="outline">
          <ShoppingCart className="h-4 w-4 mr-1.5" />
          Cart
          {cart.counts.lines > 0 && (
            <span
              data-testid="catalogue-cart-badge"
              className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-semibold bg-slate-900 text-white">
              {cart.counts.units}
            </span>
          )}
        </Button>
      </div>

      {/* L1 — Categories */}
      {level === 1 && isAdmin && (
        <div className="flex justify-end mb-3">
          <Button size="sm" onClick={() => setAddCat(true)} data-testid="cat-add-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white">
            <Plus className="h-4 w-4 mr-1" /> Add category
          </Button>
        </div>
      )}
      {level === 1 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {busy && categories.length === 0 ? (
            <div className="col-span-full text-center py-12 text-slate-500">Loading categories…</div>
          ) : categories.map((cat) => {
            const Icon = ICON[cat.icon] || Package;
            const inactive = cat.status && cat.status !== "ACTIVE";
            return (
              <div key={cat.code} data-testid={`cat-${cat.code}`}
                className={`relative text-left bg-white border rounded-md p-5 transition-all group cursor-pointer
                  ${inactive ? "border-slate-200 opacity-60" : "border-slate-200 hover:border-slate-900 hover:shadow-md"}`}
                onClick={() => goCategory(cat)}>
                {isAdmin && (
                  <div className="absolute top-3 right-3" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="text-slate-400 hover:text-slate-800 p-1" data-testid={`cat-menu-${cat.code}`}>
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setEditCat(cat)} data-testid={`cat-edit-${cat.code}`}><Pencil className="h-3.5 w-3.5 mr-2" /> Edit category</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setHistCat(cat)} data-testid={`cat-history-${cat.code}`}><HistoryIcon className="h-3.5 w-3.5 mr-2" /> View history</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => deactivateCat(cat)} data-testid={`cat-deactivate-${cat.code}`}><Power className="h-3.5 w-3.5 mr-2" /> {inactive ? "Activate" : "Deactivate"}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setConfirmDelCat(cat)} className="text-rose-600 focus:text-rose-700" data-testid={`cat-delete-${cat.code}`}><Trash2 className="h-3.5 w-3.5 mr-2" /> Delete category</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
                <div className="h-12 w-12 rounded-lg bg-slate-900 text-white grid place-items-center mb-4
                  group-hover:bg-indigo-600 transition-colors">
                  <Icon className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <div className="text-[15px] font-semibold text-slate-900 flex items-center gap-2">{cat.name}
                  {inactive && <span className="text-[8px] px-1 rounded bg-slate-200 text-slate-500">INACTIVE</span>}</div>
                <div className="text-[12px] text-slate-500 mt-1 line-clamp-2">{cat.description}</div>
                <div className="text-[11px] text-slate-400 mt-3 flex gap-3">
                  <span>{cat.brand_count} brands</span>
                  <span>·</span>
                  <span>{cat.product_count} products</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* L2 — Brands */}
      {level === 2 && category && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500">
              Select a brand
            </div>
            {isAdmin && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setLogoBackfill(true)} data-testid="brand-enrich-btn">
                  <Send className="h-4 w-4 mr-1" /> Enrich logos
                </Button>
                <Button size="sm" onClick={() => setAddBrandFor(category)} data-testid="brand-add-btn"
                  className="bg-slate-900 hover:bg-slate-800 text-white">
                  <Plus className="h-4 w-4 mr-1" /> Add brand
                </Button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {busy && brands.length === 0 ? (
              <div className="col-span-full text-center py-12 text-slate-500">Loading brands…</div>
            ) : brands.length === 0 ? (
              <div className="col-span-full text-center py-12 text-slate-500">
                {isAdmin ? "No brands linked yet — use “Add brand”." : "No brands with visible products in this category yet."}
              </div>
            ) : brands.map((br) => {
              const inactive = br.status && br.status !== "ACTIVE";
              return (
              <div key={br.code} onClick={() => goBrand(br)} role="button"
                data-testid={`brand-${br.code}`}
                className={`relative bg-white border rounded-md p-5 transition-all cursor-pointer
                  flex flex-col items-center justify-center min-h-[110px]
                  ${inactive ? "border-slate-200 opacity-60" : "border-slate-200 hover:border-slate-900 hover:shadow-md"}`}>
                {isAdmin && (
                  <div className="absolute top-2 right-2" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="text-slate-400 hover:text-slate-800 p-1" data-testid={`brand-menu-${br.code}`}>
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setEditBrand(br)} data-testid={`brand-edit-${br.code}`}><Pencil className="h-3.5 w-3.5 mr-2" /> Edit brand</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setHistBrand(br)} data-testid={`brand-history-${br.code}`}><HistoryIcon className="h-3.5 w-3.5 mr-2" /> View history</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => removeBrandFromCat(br)} data-testid={`brand-remove-${br.code}`}><X className="h-3.5 w-3.5 mr-2" /> Remove from {category.name}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => deactivateBrand(br)} data-testid={`brand-deactivate-${br.code}`}><Power className="h-3.5 w-3.5 mr-2" /> {inactive ? "Activate" : "Deactivate"}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setConfirmDelBrand(br)} className="text-rose-600 focus:text-rose-700" data-testid={`brand-delete-${br.code}`}><Trash2 className="h-3.5 w-3.5 mr-2" /> Delete brand</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
                {isAdmin && (br.verification_status === "REVIEW_REQUIRED" || br.verification_status === "UPDATE_AVAILABLE") && (
                  <span className="absolute top-2 left-2 h-2 w-2 rounded-full bg-amber-500" title="Logo review available" data-testid={`brand-logo-flag-${br.code}`} />
                )}
                <div className="h-11 w-full grid place-items-center">
                  {br.logo_url ? (
                    <img src={brandLogoSrc(br)} alt={br.name} className="max-h-10 max-w-[85%] object-contain"
                      onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <div className="h-10 px-2 min-w-10 rounded-md bg-slate-900 text-white grid place-items-center text-[13px] font-bold tracking-wide">
                      {(br.name || "?").replace(/[^A-Za-z0-9]/g, "").slice(0, 4).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="text-[13px] font-medium text-slate-900 mt-2.5 flex items-center gap-1.5">{br.name}
                  {inactive && <span className="text-[8px] px-1 rounded bg-slate-200 text-slate-500">INACTIVE</span>}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{br.product_count} products</div>
              </div>
            );})}
          </div>
        </div>
      )}

      {/* L3 — Products */}
      {level === 3 && brand && (
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-md p-3 flex items-center gap-2">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder={`Search ${brand.name} ${category.name}…`}
                className="h-9 pl-8" data-testid="product-search" />
            </div>
            <div className="text-[12px] text-slate-500">
              {products.length} product{products.length === 1 ? "" : "s"}
            </div>
            {isAdmin && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setImportOpen(true)} data-testid="model-import-btn">
                  <Upload className="h-4 w-4 mr-1" /> Import models
                </Button>
                <Button size="sm" onClick={() => setAddModel(true)} data-testid="model-add-btn"
                  className="bg-slate-900 hover:bg-slate-800 text-white">
                  <Plus className="h-4 w-4 mr-1" /> Add model
                </Button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {busy && products.length === 0 ? (
              <div className="col-span-full text-center py-12 text-slate-500">Loading products…</div>
            ) : products.length === 0 ? (
              <div className="col-span-full text-center py-12 text-slate-500">
                {isAdmin ? "No models yet — use “Add model”." : "No products match. Try changing search terms."}
              </div>
            ) : products.map((p) => {
              const inactive = p.status && p.status !== "ACTIVE";
              return (
              <div key={p.code} onClick={() => setProductSel(p)} role="button" tabIndex={0} aria-label={`Open ${p.name}`}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setProductSel(p); } }}
                data-testid={`prod-${p.code}`}
                className={`relative text-left bg-white border rounded-md p-4 transition-all cursor-pointer
                  ${inactive ? "border-slate-200 opacity-60" : "border-slate-200 hover:border-slate-900 hover:shadow-md"}`}>
                {isAdmin && (
                  <div className="absolute top-2 right-2" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="text-slate-400 hover:text-slate-800 p-1" data-testid={`prod-menu-${p.code}`}>
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setEditModel(p)} data-testid={`prod-edit-${p.code}`}><Pencil className="h-3.5 w-3.5 mr-2" /> Edit model</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => addToQuoteSingle(p)} data-testid={`prod-quickquote-${p.code}`}><FilePlus className="h-3.5 w-3.5 mr-2" /> Add to quote</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setDupModel(p)} data-testid={`prod-duplicate-${p.code}`}><Layers className="h-3.5 w-3.5 mr-2" /> Duplicate model</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setHistModel(p)} data-testid={`prod-history-${p.code}`}><HistoryIcon className="h-3.5 w-3.5 mr-2" /> View history</DropdownMenuItem>
                        {p.product_master_id && (
                          <DropdownMenuItem onClick={() => navigate(`/technology/product-master?product=${p.product_master_id}`)} data-testid={`prod-360-${p.code}`}><FileText className="h-3.5 w-3.5 mr-2" /> View Product 360</DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => deactivateModel(p)} data-testid={`prod-deactivate-${p.code}`}><Power className="h-3.5 w-3.5 mr-2" /> {inactive ? "Activate" : "Deactivate"}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setConfirmDelModel(p)} className="text-rose-600 focus:text-rose-700" data-testid={`prod-delete-${p.code}`}><Trash2 className="h-3.5 w-3.5 mr-2" /> Delete model</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-[0.1em] text-slate-500 pr-6">{brand.name} · {p.family || "—"}</div>
                <div className="text-[15px] font-semibold text-slate-900 mt-1.5 flex items-center gap-1.5">{p.name}
                  {inactive && <span className="text-[8px] px-1 rounded bg-slate-200 text-slate-500">INACTIVE</span>}</div>
                <div className="text-[11px] text-slate-500 mt-1 font-mono">{p.model || p.part_number || p.code}</div>
                {p.specs && Object.keys(p.specs).length > 0 && (
                  <div className="text-[11px] text-slate-600 mt-2 space-y-0.5">
                    {Object.entries(p.specs).slice(0, 3).map(([k, v]) => (
                      <div key={k} className="flex justify-between">
                        <span className="text-slate-400 uppercase text-[9px] tracking-wider">{k.replace(/_/g, " ")}</span>
                        <span className="ml-2 truncate">{String(v)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="border-t border-slate-100 mt-3 pt-3 flex items-end justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.1em] text-slate-500">List price</div>
                    <div className="text-[15px] font-heading font-semibold text-slate-900">
                      {fmtINR(p.list_price_inr)}
                    </div>
                  </div>
                  <div className="text-right text-[10px] text-emerald-700 uppercase font-semibold">
                    {(p.availability || "").replace(/_/g, " ")}
                  </div>
                </div>
              </div>
            );})}
          </div>
        </div>
      )}

      {productSel && (
        <ProductDrawer product={productSel} brand={brand} category={category}
          onClose={() => setProductSel(null)}
          onAddToCart={(qty) => { addToCart(productSel, qty); setProductSel(null); }}
          onAddToQuote={() => addToQuoteSingle(productSel)}
          onAddToCollection={() => setCollectFor(productSel)}
          onRequestQuote={() => {
            addToCart(productSel, 1).then(() => { setProductSel(null); setCartOpen(true); });
          }} />
      )}

      {collectFor && (
        <AddToCollectionDialog product={collectFor} onClose={() => setCollectFor(null)} />
      )}

      {cartOpen && (
        <CartDrawer
          cart={cart}
          onClose={() => setCartOpen(false)}
          onRemove={removeFromCart}
          onUpdateQty={updateQty}
          onRequestQuote={requestQuote}
        />
      )}
      {(editCat || addCat) && (
        <CategoryFormDialog cat={editCat} onClose={() => { setEditCat(null); setAddCat(false); }}
          onSaved={() => { setEditCat(null); setAddCat(false); loadCategories(); }} />
      )}
      {histCat && <CategoryHistoryDialog cat={histCat} onClose={() => setHistCat(null)} />}
      {confirmDelCat && (
        <ConfirmDialog testid="cat-delete-confirm" cancelTestid="cat-delete-cancel" confirmTestid="cat-delete-confirm-go"
          title={`Delete “${confirmDelCat.name}”?`} confirmLabel="Delete category"
          body="This permanently removes the category. It’s only possible when no products or sub-categories are assigned — otherwise you’ll be asked to reassign or deactivate."
          onConfirm={() => deleteCat(confirmDelCat)} onClose={() => setConfirmDelCat(null)} />
      )}
      {blockCat && (
        <CategoryDeleteBlocked info={blockCat} categories={categories}
          onClose={() => setBlockCat(null)}
          onReassigned={() => { setBlockCat(null); loadCategories(); }}
          onDeactivate={() => { deactivateCat(blockCat.cat); setBlockCat(null); }} />
      )}

      {/* Brand dialogs */}
      {addBrandFor && (
        <AddBrandDialog category={addBrandFor} onClose={() => setAddBrandFor(null)}
          onSaved={() => { setAddBrandFor(null); loadBrands(category); }} />
      )}
      {editBrand && (
        <BrandEditDialog brand={editBrand} onClose={() => setEditBrand(null)}
          onRefresh={() => loadBrands(category)}
          onSaved={() => { setEditBrand(null); loadBrands(category); }} />
      )}
      {histBrand && <BrandHistoryDialog brand={histBrand} onClose={() => setHistBrand(null)} />}
      {logoBackfill && (
        <BrandLogoBackfillDialog category={category} onClose={() => setLogoBackfill(false)}
          onChanged={() => loadBrands(category)} />
      )}
      {confirmDelBrand && (
        <ConfirmDialog testid="brand-delete-confirm" cancelTestid="brand-delete-cancel" confirmTestid="brand-delete-confirm-go"
          title={`Delete “${confirmDelBrand.name}”?`} confirmLabel="Delete brand"
          body="This deletes the brand globally. Only possible when no products, category links or product-master records reference it — otherwise remove it from the category or deactivate it."
          onConfirm={() => deleteBrand(confirmDelBrand)} onClose={() => setConfirmDelBrand(null)} />
      )}
      {blockBrand && (
        <BlockedDialog testid="brand-delete-blocked" title="Brand can’t be deleted" message={blockBrand.message}
          onClose={() => setBlockBrand(null)}
          actions={[
            ...(blockBrand.link_count && !blockBrand.product_count && !blockBrand.master_count ? [{
              label: `Remove & delete`, testid: "brand-blocked-remove", primary: true,
              onClick: async () => {
                await api.post(`/catalogue-v2/categories/${category.code}/brands/${blockBrand.br.code}/remove`).catch(() => {});
                await deleteBrand(blockBrand.br); setBlockBrand(null);
              } }] : [{
              label: `Remove from ${category.name}`, testid: "brand-blocked-remove",
              onClick: () => { removeBrandFromCat(blockBrand.br); setBlockBrand(null); } }]),
            { label: "Deactivate instead", testid: "brand-blocked-deactivate",
              onClick: () => { deactivateBrand(blockBrand.br); setBlockBrand(null); } },
          ]} />
      )}

      {/* Model dialogs */}
      {(addModel || editModel) && (
        <ModelFormDialog category={category} brand={brand} model={editModel}
          onClose={() => { setAddModel(false); setEditModel(null); }}
          onSaved={() => { setAddModel(false); setEditModel(null); refreshProducts(); }} />
      )}
      {dupModel && (
        <DuplicateModelDialog model={dupModel} onClose={() => setDupModel(null)}
          onSaved={() => { setDupModel(null); refreshProducts(); }} />
      )}
      {histModel && <ModelHistoryDialog model={histModel} onClose={() => setHistModel(null)} />}
      {importOpen && (
        <ModelImportDialog category={category} brand={brand}
          onClose={() => setImportOpen(false)}
          onDone={() => { setImportOpen(false); refreshProducts(); }} />
      )}
      {confirmDelModel && (
        <ConfirmDialog testid="model-delete-confirm" cancelTestid="model-delete-cancel" confirmTestid="model-delete-confirm-go"
          title={`Delete “${confirmDelModel.name}”?`} confirmLabel="Delete model"
          body="This removes the model and its Product Master record. Blocked automatically if it is used in any quote, order, stock or asset — deactivate it instead."
          onConfirm={() => deleteModel(confirmDelModel)} onClose={() => setConfirmDelModel(null)} />
      )}
      {blockModel && (
        <BlockedDialog testid="model-delete-blocked" title="Model can’t be deleted" message={blockModel.message}
          onClose={() => setBlockModel(null)}
          actions={[{ label: "Deactivate instead", testid: "model-blocked-deactivate", primary: true,
            onClick: () => { deactivateModel(blockModel.p); setBlockModel(null); } }]} />
      )}
    </div>
  );
}

function AddBrandDialog({ category, onClose, onSaved }) {
  const { open, closeThen } = useManagedDialog();
  const [f, setF] = useState({ name: "", official_domain: "", website: "", description: "" });
  const [logoFile, setLogoFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cand, setCand] = useState(null);
  const u = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) { toast.error("Brand name is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/catalogue-v2/categories/${category.code}/brands`, f);
      const code = data.brand?.code;
      if (logoFile && code) {
        const fd = new FormData(); fd.append("file", logoFile);
        await api.post(`/catalogue-v2/brands/${code}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } }).catch(() => toast.error("Brand saved, but logo upload failed"));
        toast.success(data.message || "Brand added"); closeThen(onSaved); return;
      }
      if (code && f.official_domain.trim()) {
        try {
          const r = await api.post(`/catalogue-v2/brands/${code}/logo/fetch`);
          if (r.data.found) { setCand({ code, url: `${BACKEND}${r.data.candidate_url}`, domain: r.data.domain }); setBusy(false); return; }
        } catch { /* provider off / no logo */ }
      }
      toast.success(data.message || "Brand added"); closeThen(onSaved);
    } catch (e) {
      const d = e.response?.data?._rawDetail ?? e.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "ALREADY_LINKED") { toast.info(d.message); closeThen(onSaved); }
      else toast.error((typeof d === "object" ? d.message : d) || "Save failed");
    } finally { setBusy(false); }
  };
  const approve = async () => {
    setBusy(true);
    try { await api.post(`/catalogue-v2/brands/${cand.code}/logo/approve`); toast.success("Logo approved"); closeThen(onSaved); }
    catch { toast.error("Approve failed"); setBusy(false); }
  };
  const skip = async () => { try { await api.post(`/catalogue-v2/brands/${cand.code}/logo/reject`); } catch { /* noop */ } toast.success("Brand added"); closeThen(onSaved); };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeThen(onClose); }}>
      <DialogContent data-testid="brand-form-dialog">
        {cand ? (
          <>
            <DialogHeader><DialogTitle>Logo found for {f.name}</DialogTitle></DialogHeader>
            <p className="text-[12px] text-slate-500 -mt-1">From {cand.domain} · review before it goes live.</p>
            <div className="h-28 border border-slate-200 rounded-md grid place-items-center bg-white my-2">
              <img src={cand.url} alt="candidate" className="max-h-20 max-w-[70%] object-contain" data-testid="brand-candidate-preview" />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={skip} disabled={busy} data-testid="brand-candidate-skip">Skip for now</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={approve} disabled={busy} data-testid="brand-candidate-approve">Approve &amp; use</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader><DialogTitle>Add brand to {category.name}</DialogTitle></DialogHeader>
            <p className="text-[11px] text-slate-500 -mt-1">Enter the official domain and we’ll fetch the logo for you — no Googling or cropping. Existing brands are reused (no duplicates).</p>
            <div className="space-y-3">
              <div><Label>Brand name *</Label><Input value={f.name} onChange={(e) => u("name", e.target.value)} data-testid="brand-form-name" autoFocus /></div>
              <div><Label>Official domain</Label><Input value={f.official_domain} onChange={(e) => u("official_domain", e.target.value)} placeholder="e.g. dell.com" data-testid="brand-form-domain" /></div>
              <div><Label>Website</Label><Input value={f.website} onChange={(e) => u("website", e.target.value)} placeholder="https://…" data-testid="brand-form-website" /></div>
              <LogoField file={logoFile} onFile={setLogoFile} testid="brand-form-logo" />
              <div><Label>Description</Label><Input value={f.description} onChange={(e) => u("description", e.target.value)} data-testid="brand-form-desc" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => closeThen(onClose)}>Cancel</Button>
              <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={save} disabled={busy} data-testid="brand-form-save">{busy ? "Saving…" : "Save brand"}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LogoField({ file, onFile, previewUrl, onUrl, testid, currentSrc }) {
  const preview = file ? URL.createObjectURL(file) : (currentSrc || null);
  return (
    <div>
      <Label>Brand logo</Label>
      <div className="flex items-center gap-3 mt-1">
        <div className="h-12 w-12 rounded-md border border-slate-200 grid place-items-center overflow-hidden bg-slate-50 shrink-0">
          {preview ? <img src={preview} alt="logo preview" className="max-h-10 max-w-[44px] object-contain" data-testid={`${testid}-preview`} />
            : <Upload className="h-4 w-4 text-slate-300" />}
        </div>
        <div className="flex-1 min-w-0">
          <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
            onChange={(e) => onFile(e.target.files?.[0] || null)}
            className="block w-full text-[12px] text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-[12px] file:bg-slate-900 file:text-white hover:file:bg-slate-800 cursor-pointer"
            data-testid={testid} />
          {onUrl && !file && (
            <Input value={previewUrl || ""} onChange={(e) => onUrl(e.target.value)}
              placeholder="…or paste an image URL" className="h-8 mt-1.5 text-[12px]" data-testid={`${testid}-url`} />
          )}
        </div>
        {file && <button type="button" onClick={() => onFile(null)} className="text-slate-400 hover:text-rose-600" data-testid={`${testid}-clear`}><X className="h-4 w-4" /></button>}
      </div>
      <p className="text-[10px] text-slate-400 mt-1">PNG / JPG / WEBP / SVG, up to 3 MB.</p>
    </div>
  );
}

function BrandEditDialog({ brand, onClose, onSaved, onRefresh }) {
  const { open, closeThen } = useManagedDialog();
  const [f, setF] = useState({ name: brand.name || "", official_domain: brand.official_domain || "",
    website: brand.website || "", description: brand.description || "" });
  const [busy, setBusy] = useState(false);
  const u = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) { toast.error("Brand name is required"); return; }
    setBusy(true);
    try {
      await api.patch(`/catalogue-v2/brands/${brand.code}`, { name: f.name,
        official_domain: f.official_domain, website: f.website, description: f.description });
      toast.success("Brand updated"); closeThen(onSaved);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeThen(onClose); }}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="brand-edit-dialog">
        <DialogHeader><DialogTitle>Edit brand</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Brand name</Label><Input value={f.name} onChange={(e) => u("name", e.target.value)} data-testid="brand-edit-name" /></div>
          <div><Label>Official domain</Label><Input value={f.official_domain} onChange={(e) => u("official_domain", e.target.value)} placeholder="e.g. dell.com" data-testid="brand-edit-domain" /></div>
          <div><Label>Website</Label><Input value={f.website} onChange={(e) => u("website", e.target.value)} data-testid="brand-edit-website" /></div>
          <div><Label>Description</Label><Input value={f.description} onChange={(e) => u("description", e.target.value)} data-testid="brand-edit-desc" /></div>
        </div>
        <BrandLogoManager brand={brand} domain={f.official_domain} onChanged={onRefresh} />
        <DialogFooter>
          <Button variant="outline" onClick={() => closeThen(onClose)} data-testid="brand-edit-cancel">Cancel</Button>
          <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={save} disabled={busy} data-testid="brand-edit-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const LOGO_STATUS_STYLE = {
  APPROVED: "bg-emerald-100 text-emerald-700", MANUAL: "bg-indigo-100 text-indigo-700",
  REVIEW_REQUIRED: "bg-amber-100 text-amber-700", UPDATE_AVAILABLE: "bg-amber-100 text-amber-700",
  NOT_FOUND: "bg-slate-100 text-slate-500", CANDIDATE_FOUND: "bg-amber-100 text-amber-700",
};

function BrandLogoManager({ brand, domain, onChanged }) {
  const [status, setStatus] = useState(brand.verification_status || (brand.logo_object ? "APPROVED" : "NOT_FOUND"));
  const [liveSrc, setLiveSrc] = useState(brandLogoSrc(brand));
  const [cand, setCand] = useState(null);
  const [manual, setManual] = useState(!!brand.manual_override);
  const [logoFile, setLogoFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fetchLogo = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/catalogue-v2/brands/${brand.code}/logo/refresh`);
      if (data.found) { setCand(`${BACKEND}${data.candidate_url}`); setStatus(data.status); toast.success(`Logo found (${data.domain})`); }
      else toast.info(data.reason === "no_domain" ? "Add an official domain first" : "No logo found for this brand");
    } catch (e) { toast.error(e.response?.data?.detail || "Fetch failed"); }
    finally { setBusy(false); }
  };
  const approve = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/catalogue-v2/brands/${brand.code}/logo/approve`);
      setLiveSrc(`${BACKEND}${data.logo_url}`); setStatus("APPROVED"); setManual(false); setCand(null);
      toast.success("Logo approved & live"); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    try { const { data } = await api.post(`/catalogue-v2/brands/${brand.code}/logo/reject`); setStatus(data.verification_status); } catch { /* noop */ }
    setCand(null);
  };
  const upload = async () => {
    if (!logoFile) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", logoFile);
      const { data } = await api.post(`/catalogue-v2/brands/${brand.code}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLiveSrc(`${BACKEND}${data.logo_url}`); setStatus("MANUAL"); setManual(true); setCand(null); setLogoFile(null);
      toast.success("Logo uploaded (manual override)"); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-slate-200 rounded-md p-3 space-y-3" data-testid="brand-logo-manager">
      <div className="flex items-center justify-between">
        <Label className="mb-0">Brand logo</Label>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${LOGO_STATUS_STYLE[status] || "bg-slate-100 text-slate-500"}`} data-testid="brand-logo-status">
          {(status || "NOT_FOUND").replace(/_/g, " ")}{manual && status !== "MANUAL" ? " · MANUAL" : ""}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <div className="h-14 w-20 border border-slate-200 rounded grid place-items-center bg-white shrink-0">
          {liveSrc ? <img src={liveSrc} alt="current logo" className="max-h-11 max-w-[70px] object-contain" data-testid="brand-logo-current" />
            : <span className="text-[13px] font-bold text-slate-700">{(brand.name || "?").slice(0, 4).toUpperCase()}</span>}
        </div>
        {cand && (
          <>
            <span className="text-slate-300 text-lg">→</span>
            <div className="h-14 w-20 border-2 border-amber-300 rounded grid place-items-center bg-white shrink-0">
              <img src={cand} alt="candidate" className="max-h-11 max-w-[70px] object-contain" data-testid="brand-logo-candidate" />
            </div>
          </>
        )}
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={fetchLogo} disabled={busy} data-testid="brand-logo-fetch">
          <Send className="h-3.5 w-3.5 mr-1" /> {liveSrc ? "Check for update" : "Fetch from web"}
        </Button>
      </div>
      {cand && (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={reject} disabled={busy} data-testid="brand-logo-reject">Reject</Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={approve} disabled={busy} data-testid="brand-logo-approve">
            {liveSrc ? "Use new logo" : "Approve & use"}
          </Button>
        </div>
      )}
      <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
        <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
          onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
          className="flex-1 text-[11px] file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-slate-200 file:text-[11px] hover:file:bg-slate-300"
          data-testid="brand-logo-upload-file" />
        <Button size="sm" variant="outline" onClick={upload} disabled={!logoFile || busy} data-testid="brand-logo-upload-btn">
          <Upload className="h-3.5 w-3.5 mr-1" /> Upload
        </Button>
      </div>
      <p className="text-[10px] text-slate-400">Uploading sets a manual override — automatic refresh will only flag updates, never replace it.</p>
    </div>
  );
}

function BrandHistoryDialog({ brand, onClose }) {
  const { open, closeThen } = useManagedDialog();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/catalogue-v2/brands/${brand.code}/history`).then(({ data }) => setData(data)).catch(() => setData({ events: [] })); }, [brand.code]);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeThen(onClose); }}>
      <DialogContent data-testid="brand-history-dialog">
        <DialogHeader><DialogTitle>History · {brand.name}</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[55vh] overflow-y-auto text-[12px]">
          {(data?.events || []).map((e, i) => (
            <div key={i} className="border border-slate-200 rounded p-2">
              <div className="flex gap-2"><span className="font-semibold">{e.event}</span><span className="text-slate-500">{e.by}</span><span className="ml-auto text-slate-400">{(e.at || "").slice(0, 16).replace("T", " ")}</span></div>
              {e.old && <div className="text-rose-600">Old: {JSON.stringify(e.old)}</div>}
              {e.new && <div className="text-emerald-700">New: {JSON.stringify(e.new)}</div>}
            </div>
          ))}
          {data && !data.events?.length && <div className="text-slate-400">No history.</div>}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => closeThen(onClose)} data-testid="brand-history-close">Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BrandLogoBackfillDialog({ category, onClose, onChanged }) {
  const { open, closeThen } = useManagedDialog();
  const [data, setData] = useState(null);
  const [busyCode, setBusyCode] = useState(null);
  const [busyAll, setBusyAll] = useState(false);
  const [cands, setCands] = useState({});
  const [quoteLogos, setQuoteLogos] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const load = useCallback(async () => {
    setData(null);
    try { const { data } = await api.get(`/catalogue-v2/brands/logo/backfill`, { params: { category_code: category.code } }); setData(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Load failed"); setData({ items: [] }); }
  }, [category.code]);
  useEffect(() => { load();
    api.get(`/commercial/company-profile`).then(({ data }) => setQuoteLogos(!!data.quote_show_brand_logos)).catch(() => setQuoteLogos(false));
    /* eslint-disable-next-line */ }, [load]);
  const toggleQuoteLogos = async () => {
    const next = !quoteLogos; setQuoteLogos(next);
    try { await api.put(`/commercial/company-profile`, { quote_show_brand_logos: next }); toast.success(next ? "Brand logos will show on quotes" : "Brand logos hidden on quotes"); }
    catch { setQuoteLogos(!next); toast.error("Could not save setting"); }
  };
  const runAll = async () => {
    setBusyAll(true);
    try { const { data } = await api.post(`/catalogue-v2/brands/logo/backfill/run`, null, { params: { category_code: category.code, only_missing: true } });
      toast.success(`Approved ${data.approved} · skipped ${data.skipped}${data.failed ? ` · ${data.failed} needed review` : ""}`);
      onChanged && onChanged(); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Bulk enrich failed"); }
    finally { setBusyAll(false); }
  };
  const fetchRow = async (code) => {
    setBusyCode(code);
    try { const { data } = await api.post(`/catalogue-v2/brands/${code}/logo/refresh`);
      if (data.found) { setCands((s) => ({ ...s, [code]: `${BACKEND}${data.candidate_url}` })); }
      else toast.info(data.reason === "no_domain" ? "No domain resolved" : "No logo found"); }
    catch (e) { toast.error(e.response?.data?.detail || "Fetch failed"); }
    finally { setBusyCode(null); }
  };
  const approveRow = async (code) => {
    setBusyCode(code);
    try { await api.post(`/catalogue-v2/brands/${code}/logo/approve`);
      toast.success("Logo approved"); setCands((s) => { const n = { ...s }; delete n[code]; return n; });
      onChanged && onChanged(); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
    finally { setBusyCode(null); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeThen(onClose); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="brand-backfill-dialog">
        <DialogHeader><DialogTitle>Brand logo enrichment — {category.name}</DialogTitle></DialogHeader>
        {!data ? <div className="py-10 text-center text-slate-500">Resolving domains…</div> : (
          <>
            {!data.provider_configured && <p className="text-[12px] text-amber-700 bg-amber-50 rounded p-2">Logo provider not configured — set LOGODEV_SECRET to enable automatic fetch.</p>}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <label className="flex items-center gap-2 text-[12px] text-slate-600 cursor-pointer" data-testid="quote-logos-toggle">
                <input type="checkbox" checked={!!quoteLogos} onChange={toggleQuoteLogos} className="h-4 w-4 accent-slate-900" />
                Show approved brand logos on quotation PDFs
              </label>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowPreview((s) => !s)} data-testid="quote-logos-preview-btn">
                  <FileText className="h-3.5 w-3.5 mr-1" /> {showPreview ? "Hide preview" : "Preview on quote"}
                </Button>
                <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" disabled={busyAll || !data.provider_configured} onClick={runAll} data-testid="backfill-run-all">
                  <Send className="h-3.5 w-3.5 mr-1" /> {busyAll ? "Enriching…" : "Fetch & approve all high-confidence"}
                </Button>
              </div>
            </div>
            {showPreview && (
              <div className="border border-slate-200 rounded-md bg-white p-3" data-testid="quote-logos-preview">
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 mb-2">Quotation line preview</div>
                {data.items.filter((r) => r.has_logo).slice(0, 2).length === 0 ? (
                  <div className="text-[12px] text-slate-500">Approve at least one brand logo to see the preview.</div>
                ) : data.items.filter((r) => r.has_logo).slice(0, 2).map((r, i) => (
                  <div key={r.code} className={`py-2.5 ${i > 0 ? "border-t border-slate-100" : ""}`}>
                    <div className="flex justify-between items-start">
                      <div className="font-heading font-semibold text-[12px] text-slate-900">{r.brand} — sample product</div>
                      <div className="text-[12px] text-slate-900">₹1,20,000</div>
                    </div>
                    <div className="text-[11px] italic text-slate-500">High-performance business unit · 3-year warranty</div>
                    <div className="text-[11px] text-slate-700">Part #: {r.code}-SAMPLE-001</div>
                    <img src={`${BACKEND}/api/catalogue-v2/brands/${r.code}/logo`} alt={r.brand}
                      className="h-4 max-w-[64px] object-contain mt-1.5" data-testid={`quote-preview-logo-${r.code}`} />
                  </div>
                ))}
                <div className="text-[10px] text-slate-400 mt-1">This is how the brand logo appears beneath each product line on the PDF.</div>
              </div>
            )}
            <div className="border border-slate-200 rounded-md max-h-[56vh] overflow-y-auto">
              {data.items.map((r) => (
                <div key={r.code} className="flex items-center gap-3 px-3 py-2 border-b border-slate-100 text-[12px]" data-testid={`backfill-row-${r.code}`}>
                  <span className="w-32 truncate font-medium">{r.brand}</span>
                  <span className="w-28 truncate text-slate-500 font-mono">{r.official_domain || "—"}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] ${LOGO_STATUS_STYLE[r.status] || "bg-slate-100 text-slate-500"}`}>{(r.status || "").replace(/_/g, " ")}</span>
                  {cands[r.code] && <img src={cands[r.code]} alt="cand" className="h-7 max-w-[70px] object-contain border border-amber-300 rounded bg-white" />}
                  <div className="flex-1" />
                  {r.manual_override ? <span className="text-[11px] text-indigo-600">manual — kept</span> : cands[r.code] ? (
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7" disabled={busyCode === r.code} onClick={() => approveRow(r.code)} data-testid={`backfill-approve-${r.code}`}>Approve</Button>
                  ) : (
                    <Button size="sm" variant="outline" className="h-7" disabled={busyCode === r.code || !data.provider_configured} onClick={() => fetchRow(r.code)} data-testid={`backfill-fetch-${r.code}`}>{busyCode === r.code ? "…" : (r.has_logo ? "Check" : "Fetch")}</Button>
                  )}
                </div>
              ))}
            </div>
            <p className="text-[10px] text-slate-400">“Fetch &amp; approve all” only publishes confident domain matches and never touches manual overrides.</p>
          </>
        )}
        <DialogFooter><Button variant="outline" onClick={() => closeThen(onClose)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SPEC_FIELDS = {
  LAPTOPS: ["processor", "memory", "storage", "display", "graphics", "operating_system"],
  DESKTOPS: ["processor", "memory", "storage", "graphics", "operating_system"],
  MONITORS: ["size", "resolution", "panel", "refresh_rate", "ports"],
  SERVERS: ["processor", "memory", "storage", "form_factor"],
  STORAGE: ["capacity", "type", "interface"],
  NETWORKING: ["ports", "speed", "poe", "wireless_standard"],
  PRINTERS: ["technology", "speed_ppm", "duplex", "connectivity"],
  UPS: ["capacity_va", "topology"],
  AV: ["type", "connectivity"],
};

function ModelFormDialog({ category, brand, model, onClose, onSaved }) {
  const isEdit = !!model;
  const { open, closeThen } = useManagedDialog();
  const [f, setF] = useState({
    product_name: model?.name || "", sku_oem: model?.sku || model?.part_number || "",
    description: model?.description || "", hsn_sac: model?.hsn_sac || "",
    gst_percent: model?.gst_percent ?? "", list_price_inr: model?.list_price_inr ?? "",
    warranty: model?.warranty_months ? `${model.warranty_months} Months` : "",
    image_url: model?.image_url || "",
  });
  const [specs, setSpecs] = useState(model?.specs || {});
  const [showSpecs, setShowSpecs] = useState(!!(model?.specs && Object.keys(model.specs).length));
  const [busy, setBusy] = useState(false);
  const u = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const specKeys = SPEC_FIELDS[category?.code] || SPEC_FIELDS.LAPTOPS;
  const save = async () => {
    if (!f.product_name.trim() || !f.sku_oem.trim() || !f.description.trim()) {
      toast.error("Product name, SKU / OEM Part # and description are required"); return;
    }
    setBusy(true);
    const payload = {
      product_name: f.product_name, sku_oem: f.sku_oem, description: f.description,
      hsn_sac: f.hsn_sac || null, gst_percent: f.gst_percent === "" ? null : Number(f.gst_percent),
      list_price_inr: f.list_price_inr === "" ? null : Number(f.list_price_inr),
      warranty: f.warranty || null, image_url: f.image_url || null,
      specs: Object.fromEntries(Object.entries(specs).filter(([, v]) => String(v).trim())),
    };
    try {
      if (isEdit) await api.patch(`/catalogue-v2/products/${model.code}`, payload);
      else await api.post(`/catalogue-v2/categories/${category.code}/brands/${brand.code}/models`, payload);
      toast.success(isEdit ? "Model updated" : "Model added"); closeThen(onSaved);
    } catch (e) {
      const d = e.response?.data?._rawDetail ?? e.response?.data?.detail;
      toast.error((typeof d === "object" ? d.message : d) || "Save failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeThen(onClose); }}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="model-form-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Edit model" : "Add model"}</DialogTitle></DialogHeader>
        <div className="text-[11px] text-slate-500 -mt-1">
          Brand <b>{brand?.name}</b> · Category <b>{category?.name}</b> (auto-filled)
        </div>
        <div className="space-y-3">
          <div><Label>Product / Model name *</Label><Input value={f.product_name} onChange={(e) => u("product_name", e.target.value)} placeholder="e.g. Latitude 5550" data-testid="model-form-name" autoFocus /></div>
          <div><Label>SKU / OEM Part # *</Label><Input value={f.sku_oem} onChange={(e) => u("sku_oem", e.target.value)} data-testid="model-form-sku" /></div>
          <div><Label>Description *</Label><Input value={f.description} onChange={(e) => u("description", e.target.value)} data-testid="model-form-desc" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>HSN code</Label>
              <HsnPicker value={f.hsn_sac} productName={f.product_name} productDesc={f.description}
                type="HSN" testid="model-form-hsn"
                onChange={(code, item) => {
                  u("hsn_sac", code || "");
                  const g = item?.gst_percent ?? item?.gst_rate ?? item?.gst;
                  if (g !== undefined && g !== null && g !== "") u("gst_percent", g);
                }} />
            </div>
            <div><Label>GST %</Label><Input type="number" value={f.gst_percent} onChange={(e) => u("gst_percent", e.target.value)} data-testid="model-form-gst" /></div>
            <div><Label>List / selling price</Label><Input type="number" value={f.list_price_inr} onChange={(e) => u("list_price_inr", e.target.value)} data-testid="model-form-price" /></div>
            <div><Label>Warranty</Label><Input value={f.warranty} onChange={(e) => u("warranty", e.target.value)} placeholder="e.g. 3 Years" /></div>
          </div>
          <div><Label>Product image URL</Label><Input value={f.image_url} onChange={(e) => u("image_url", e.target.value)} placeholder="https://…" /></div>
          <button type="button" onClick={() => setShowSpecs((s) => !s)} className="text-[12px] text-indigo-600 font-medium" data-testid="model-form-specs-toggle">
            {showSpecs ? "− Hide" : "+ Add"} catalogue specifications
          </button>
          {showSpecs && (
            <div className="grid grid-cols-2 gap-3 border-t border-slate-100 pt-3">
              {specKeys.map((k) => (
                <div key={k}><Label className="capitalize">{k.replace(/_/g, " ")}</Label>
                  <Input value={specs[k] || ""} onChange={(e) => setSpecs((s) => ({ ...s, [k]: e.target.value }))} /></div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => closeThen(onClose)}>Cancel</Button>
          <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={save} disabled={busy} data-testid="model-form-save">{busy ? "Saving…" : (isEdit ? "Save" : "Save model")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DuplicateModelDialog({ model, onClose, onSaved }) {
  const { open, closeThen } = useManagedDialog();
  const [f, setF] = useState({ product_name: `${model.name} (Copy)`, sku_oem: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.product_name.trim() || !f.sku_oem.trim()) { toast.error("New name and SKU / OEM Part # are required"); return; }
    setBusy(true);
    try { await api.post(`/catalogue-v2/products/${model.code}/duplicate`, f);
      toast.success("Model duplicated"); closeThen(onSaved); }
    catch (e) { const d = e.response?.data?._rawDetail ?? e.response?.data?.detail;
      toast.error((typeof d === "object" ? d.message : d) || "Duplicate failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeThen(onClose); }}>
      <DialogContent data-testid="model-duplicate-dialog">
        <DialogHeader><DialogTitle>Duplicate “{model.name}”</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>New product / model name *</Label><Input value={f.product_name} onChange={(e) => setF((s) => ({ ...s, product_name: e.target.value }))} data-testid="model-dup-name" /></div>
          <div><Label>New SKU / OEM Part # *</Label><Input value={f.sku_oem} onChange={(e) => setF((s) => ({ ...s, sku_oem: e.target.value }))} data-testid="model-dup-sku" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => closeThen(onClose)}>Cancel</Button>
          <Button className="bg-slate-900 text-white" onClick={save} disabled={busy} data-testid="model-dup-save">{busy ? "Copying…" : "Duplicate"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ModelImportDialog({ category, brand, onClose, onDone }) {
  const { open, closeThen } = useManagedDialog();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const base = `/catalogue-v2/categories/${category.code}/brands/${brand.code}/models/import`;
  const downloadTemplate = async () => {
    try {
      const { data } = await api.get(`/catalogue-v2/models/import/template`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = "catalogue-models-template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  const doUpload = async (commit) => {
    if (!file) { toast.error("Choose a CSV file"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`${base}?dry_run=${!commit}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.dry_run) { setPreview(data); if (!data.valid) toast.info("No valid rows — fix the errors and re-upload."); }
      else { toast.success(`Imported ${data.created} model(s)` + (data.error_count ? `, ${data.error_count} failed` : "")); closeThen(onDone); }
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeThen(onClose); }}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="model-import-dialog">
        <DialogHeader><DialogTitle>Import models — {brand?.name} · {category?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-[12px] text-slate-600">
            Each row becomes a Product Master record + catalogue listing. Brand and category are set from this screen.
            <button onClick={downloadTemplate} className="ml-1 text-indigo-600 font-medium hover:underline" data-testid="model-import-template">Download CSV template</button>
          </div>
          <input type="file" accept=".csv,text/csv" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
            className="block w-full text-[12px] file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-slate-900 file:text-white hover:file:bg-slate-800"
            data-testid="model-import-file" />
          {preview && (
            <div className="border border-slate-200 rounded-md">
              <div className="flex gap-4 px-3 py-2 text-[12px] bg-slate-50 border-b">
                <span>Rows: <b>{preview.total}</b></span>
                <span className="text-emerald-700">Valid: <b>{preview.valid}</b></span>
                <span className="text-rose-600">Errors: <b>{preview.error_count}</b></span>
              </div>
              <div className="max-h-64 overflow-y-auto text-[11px]">
                {preview.preview.map((r) => (
                  <div key={r.line} className={`flex gap-2 px-3 py-1.5 border-b border-slate-100 ${r.ok ? "" : "bg-rose-50"}`} data-testid={`model-import-row-${r.line}`}>
                    <span className="text-slate-400 w-8">#{r.line}</span>
                    <span className="flex-1 truncate">{r.product_name}</span>
                    <span className="font-mono text-slate-500 w-28 truncate">{r.sku_oem}</span>
                    <span className={r.ok ? "text-emerald-700" : "text-rose-600 truncate"}>{r.ok ? "OK" : r.errors.join("; ")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => closeThen(onClose)}>Cancel</Button>
          {!preview || preview.error_count > 0 ? (
            <Button className="bg-slate-900 text-white" onClick={() => doUpload(false)} disabled={busy} data-testid="model-import-validate">{busy ? "Checking…" : "Upload & preview"}</Button>
          ) : (
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => doUpload(true)} disabled={busy} data-testid="model-import-commit">{busy ? "Importing…" : `Import ${preview.valid} model(s)`}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ModelHistoryDialog({ model, onClose }) {
  const { open, closeThen } = useManagedDialog();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/catalogue-v2/products/${model.code}/history`).then(({ data }) => setData(data)).catch(() => setData({ events: [] })); }, [model.code]);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeThen(onClose); }}>
      <DialogContent data-testid="model-history-dialog">
        <DialogHeader><DialogTitle>History · {model.name}</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[55vh] overflow-y-auto text-[12px]">
          {(data?.events || []).map((e, i) => (
            <div key={i} className="border border-slate-200 rounded p-2">
              <div className="flex gap-2"><span className="font-semibold">{e.event}</span><span className="text-slate-500">{e.by}</span><span className="ml-auto text-slate-400">{(e.at || "").slice(0, 16).replace("T", " ")}</span></div>
              {e.old && <div className="text-rose-600 break-all">Old: {JSON.stringify(e.old)}</div>}
              {e.new && <div className="text-emerald-700 break-all">New: {JSON.stringify(e.new)}</div>}
            </div>
          ))}
          {data && !data.events?.length && <div className="text-slate-400">No history.</div>}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => closeThen(onClose)} data-testid="model-history-close">Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoryFormDialog({ cat, onClose, onSaved }) {
  const isEdit = !!cat;
  const { open, closeThen } = useManagedDialog();
  const [f, setF] = useState({
    code: cat?.code || "", name: cat?.name || "", description: cat?.description || "",
    icon: cat?.icon || "", display_order: cat?.display_order ?? 0,
  });
  const [busy, setBusy] = useState(false);
  const u = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.name.trim() || (!isEdit && !f.code.trim())) { toast.error("Code and name are required"); return; }
    setBusy(true);
    try {
      if (isEdit) await api.patch(`/catalogue-v2/categories/${cat.code}`, {
        name: f.name, description: f.description, icon: f.icon, display_order: Number(f.display_order) });
      else await api.post("/catalogue-v2/categories", {
        code: f.code.trim().toUpperCase(), name: f.name, description: f.description,
        icon: f.icon, display_order: Number(f.display_order) });
      toast.success(isEdit ? "Category updated" : "Category created"); closeThen(onSaved);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeThen(onClose); }}>
      <DialogContent data-testid="cat-form-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Edit category" : "Add category"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {!isEdit && <div><Label>Code</Label><Input value={f.code} onChange={(e) => u("code", e.target.value)} data-testid="cat-form-code" /></div>}
          <div className={isEdit ? "col-span-2" : ""}><Label>Name</Label><Input value={f.name} onChange={(e) => u("name", e.target.value)} data-testid="cat-form-name" /></div>
          <div className="col-span-2"><Label>Description</Label><Input value={f.description} onChange={(e) => u("description", e.target.value)} data-testid="cat-form-desc" /></div>
          <div><Label>Icon key</Label><Input value={f.icon} onChange={(e) => u("icon", e.target.value)} placeholder="laptop, monitor…" /></div>
          <div><Label>Display order</Label><Input type="number" value={f.display_order} onChange={(e) => u("display_order", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => closeThen(onClose)}>Cancel</Button>
          <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={save} disabled={busy} data-testid="cat-form-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoryHistoryDialog({ cat, onClose }) {
  const { open, closeThen } = useManagedDialog();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/catalogue-v2/categories/${cat.code}/history`).then(({ data }) => setData(data)).catch(() => setData({ events: [] })); }, [cat.code]);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeThen(onClose); }}>
      <DialogContent data-testid="cat-history-dialog">
        <DialogHeader><DialogTitle>History · {cat.name}</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[55vh] overflow-y-auto text-[12px]">
          {(data?.events || []).map((e, i) => (
            <div key={i} className="border border-slate-200 rounded p-2">
              <div className="flex gap-2"><span className="font-semibold">{e.event}</span><span className="text-slate-500">{e.by}</span><span className="ml-auto text-slate-400">{(e.at || "").slice(0, 16).replace("T", " ")}</span></div>
              {e.old && <div className="text-rose-600">Old: {JSON.stringify(e.old)}</div>}
              {e.new && <div className="text-emerald-700">New: {JSON.stringify(e.new)}</div>}
            </div>
          ))}
          {data && !data.events?.length && <div className="text-slate-400">No history.</div>}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => closeThen(onClose)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoryDeleteBlocked({ info, categories, onClose, onReassigned, onDeactivate }) {
  const { open, closeThen } = useManagedDialog();
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const reassign = async () => {
    if (!target) { toast.error("Pick a destination category"); return; }
    setBusy(true);
    try { const { data } = await api.post(`/catalogue-v2/categories/${info.cat.code}/reassign`, null, { params: { target_code: target } });
      toast.success(`Moved ${data.moved} product(s)`); closeThen(onReassigned); }
    catch (e) { toast.error(e.response?.data?.detail || "Reassign failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeThen(onClose); }}>
      <DialogContent data-testid="cat-delete-blocked">
        <DialogHeader><DialogTitle>Category can’t be deleted</DialogTitle></DialogHeader>
        <p className="text-[13px] text-slate-600">{info.message}</p>
        <div className="mt-2">
          <Label>Reassign products to</Label>
          <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="cat-reassign-target"
            className="w-full h-9 border border-slate-200 rounded px-2 text-[13px] bg-white">
            <option value="">Select category…</option>
            {categories.filter((c) => c.code !== info.cat.code && (!c.status || c.status === "ACTIVE")).map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => closeThen(onDeactivate)} data-testid="cat-blocked-deactivate">Deactivate instead</Button>
          <Button className="bg-slate-900 text-white" onClick={reassign} disabled={busy} data-testid="cat-reassign-go">{busy ? "Moving…" : "Reassign products"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ProductDrawer({ product, brand, category, onClose, onAddToCart, onRequestQuote, onAddToQuote, onAddToCollection }) {
  const [qty, setQty] = useState(1);
  return (
    <A11yDrawer labelId="product-drawer-title" onClose={onClose} testid="product-drawer">
        <button onClick={onClose} data-autofocus aria-label="Close product details" className="text-[12px] text-slate-500 hover:text-slate-900" data-testid="product-drawer-close">
          ✕ Close
        </button>
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
            {category?.name} · {brand?.name}
          </div>
          <h2 id="product-drawer-title" className="text-2xl font-heading font-semibold text-slate-900 mt-1">{product.name}</h2>
          <div className="text-[12px] text-slate-500 mt-1 font-mono">
            {product.part_number || product.code}
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-2 text-[13px]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.1em] text-slate-500">List price</div>
            <div className="text-[18px] font-heading font-semibold text-slate-900 mt-1">
              {fmtINR(product.list_price_inr)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.1em] text-slate-500">Availability</div>
            <div className="text-[13px] text-emerald-700 mt-1 font-medium">
              {(product.availability || "").replace(/_/g, " ")}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              Lead time: {product.lead_time_days || "—"} days
            </div>
          </div>
        </div>
        {product.specs && Object.keys(product.specs).length > 0 && (
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-2">Specifications</div>
            <div className="border border-slate-200 rounded-md divide-y">
              {Object.entries(product.specs).map(([k, v]) => (
                <div key={k} className="flex justify-between px-3 py-2 text-[13px]">
                  <span className="text-slate-500 uppercase text-[10px] tracking-wider">{k.replace(/_/g, " ")}</span>
                  <span className="text-slate-800">{String(v)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-2">Coverage</div>
          <div className="text-[13px] text-slate-700">
            Manufacturer warranty: <strong>{product.warranty_months || 12} months</strong>
          </div>
        </div>
        <div className="mt-6 flex gap-2 items-center">
          <div className="flex items-center gap-1 border border-slate-200 rounded h-10 px-2">
            <button onClick={() => setQty(Math.max(1, qty - 1))}
              className="h-8 w-8 text-slate-600 hover:text-slate-900"
              data-testid="prod-qty-dec">–</button>
            <input value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-12 text-center text-[13px] bg-transparent focus:outline-none"
              data-testid="prod-qty-input" />
            <button onClick={() => setQty(qty + 1)}
              className="h-8 w-8 text-slate-600 hover:text-slate-900"
              data-testid="prod-qty-inc">+</button>
          </div>
          <Button className="flex-1 h-10 bg-slate-900 text-white hover:bg-slate-800"
            onClick={() => onAddToCart?.(qty)}
            data-testid="prod-request">
            <ShoppingCart className="h-4 w-4 mr-1.5" />
            Add to cart
          </Button>
          <Button variant="outline" className="h-10"
            onClick={() => onRequestQuote?.()}
            data-testid="prod-quote">
            Request quote
          </Button>
        </div>
        <div className="mt-3 flex gap-2 border-t border-slate-100 pt-3">
          <Button variant="outline" className="flex-1 h-10"
            onClick={() => onAddToQuote?.()} data-testid="prod-add-to-quote">
            <FileText className="h-4 w-4 mr-1.5" /> Add to Quote
          </Button>
          <Button variant="outline" className="flex-1 h-10"
            onClick={() => onAddToCollection?.()} data-testid="prod-add-to-collection">
            <Layers className="h-4 w-4 mr-1.5" /> Add to Collection
          </Button>
        </div>
    </A11yDrawer>
  );
}


function AddToCollectionDialog({ product, onClose }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const load = useCallback(async () => {
    setBusy(true);
    try { const { data } = await api.get("/catalogue-collections"); setRows(data.items || []); }
    catch { setRows([]); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addTo = async (cid, name) => {
    try {
      await api.post(`/catalogue-collections/${cid}/items`, { product_code: product.code, quantity: 1 });
      toast.success(`Added to ${name}`);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
  };
  const createAndAdd = async () => {
    if (!newName.trim()) { toast.error("Enter a name"); return; }
    try {
      const { data } = await api.post("/catalogue-collections", { name: newName.trim() });
      await addTo(data.collection.id, data.collection.name);
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
      onClick={onClose} data-testid="add-to-collection-dialog">
      <div className="bg-white rounded-md w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-heading font-semibold text-slate-900">Add to collection</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X className="h-5 w-5" /></button>
        </div>
        <div className="text-[12px] text-slate-500 mt-1 truncate">{product.name}</div>

        <div className="mt-4 max-h-64 overflow-auto space-y-1.5">
          {busy && rows.length === 0 ? (
            <div className="text-center py-6 text-slate-500 text-[13px]">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-6 text-slate-500 text-[13px]">No collections yet — create one below.</div>
          ) : rows.map((c) => (
            <button key={c.id} onClick={() => addTo(c.id, c.name)}
              className="w-full text-left border border-slate-200 rounded-md px-3 py-2 hover:border-slate-900 flex items-center justify-between"
              data-testid={`collect-pick-${c.id}`}>
              <span className="text-[13px] font-medium text-slate-900 truncate">{c.name}</span>
              <span className="text-[11px] text-slate-400">{c.line_count} items</span>
            </button>
          ))}
        </div>

        {creating ? (
          <div className="mt-4 flex gap-2">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus
              placeholder="New collection name" className="h-9" data-testid="collect-new-name" />
            <Button className="h-9 bg-slate-900 text-white hover:bg-slate-800" onClick={createAndAdd}
              data-testid="collect-new-save">Create</Button>
          </div>
        ) : (
          <button onClick={() => setCreating(true)}
            className="mt-4 text-[13px] text-emerald-700 font-medium hover:underline inline-flex items-center gap-1"
            data-testid="collect-new-toggle">
            <Plus className="h-3.5 w-3.5" /> New collection
          </button>
        )}
        <div className="mt-4">
          <button onClick={() => { onClose(); navigate("/sales-desk/collections"); }}
            className="text-[11px] text-slate-500 hover:text-slate-900">Manage collections →</button>
        </div>
      </div>
    </div>
  );
}


function CartDrawer({ cart, onClose, onRemove, onUpdateQty, onRequestQuote }) {
  const [notes, setNotes] = useState("");
  const [country, setCountry] = useState("");
  const [requiredBy, setRequiredBy] = useState("");
  const items = cart?.items || [];
  return (
    <A11yDrawer labelId="cart-drawer-title" onClose={onClose} testid="cart-drawer">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
              Catalogue · Cart
            </div>
            <h2 id="cart-drawer-title" className="text-2xl font-heading font-semibold text-slate-900 mt-1">
              Your cart
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"
            data-testid="cart-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="mt-8 text-center py-12 text-[13px] text-slate-500 border border-dashed rounded-md"
            data-testid="cart-empty">
            <ShoppingCart className="h-8 w-8 mx-auto text-slate-300 mb-2" />
            Your cart is empty. Add products from the catalogue.
          </div>
        ) : (
          <>
            <div className="mt-4 space-y-2">
              {items.map((it) => (
                <div key={it.product_code}
                  data-testid={`cart-item-${it.product_code}`}
                  className="border border-slate-200 rounded-md p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[14px] font-medium text-slate-900 truncate">
                        {it.name || it.product_code}
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono">
                        {it.part_number || it.product_code}
                      </div>
                      {it.entitlement_status === "NOT_ENTITLED" && (
                        <div className="mt-1 inline-block text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200"
                          data-testid={`cart-not-entitled-${it.product_code}`}>
                          Not in current contract — quote required
                        </div>
                      )}
                    </div>
                    <button onClick={() => onRemove?.(it.product_code)}
                      className="h-7 w-7 rounded hover:bg-slate-100 text-slate-400 hover:text-rose-600 grid place-items-center"
                      data-testid={`cart-remove-${it.product_code}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center gap-1 border border-slate-200 rounded h-8 px-1">
                      <button onClick={() => onUpdateQty?.(it.product_code, it.quantity - 1)}
                        className="h-6 w-6 text-slate-600 hover:text-slate-900"
                        data-testid={`cart-dec-${it.product_code}`}>–</button>
                      <span className="w-8 text-center text-[12px]">{it.quantity}</span>
                      <button onClick={() => onUpdateQty?.(it.product_code, it.quantity + 1)}
                        className="h-6 w-6 text-slate-600 hover:text-slate-900"
                        data-testid={`cart-inc-${it.product_code}`}>+</button>
                    </div>
                    <div className="text-[13px] font-mono text-slate-800">
                      {fmtINR(it.list_price_inr)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 border-t pt-3 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.1em] text-slate-500">Subtotal (indicative)</div>
              <div className="text-[18px] font-heading font-semibold text-slate-900"
                data-testid="cart-subtotal">
                {fmtINR(cart?.subtotal_inr)}
              </div>
            </div>

            <div className="mt-5 space-y-2.5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.1em] text-slate-500 mb-1">Delivery country (optional)</div>
                <Input value={country} onChange={(e) => setCountry(e.target.value)}
                  placeholder="IN, SG, US…" className="h-9"
                  data-testid="cart-country" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.1em] text-slate-500 mb-1">Required by (optional)</div>
                <Input type="date" value={requiredBy}
                  onChange={(e) => setRequiredBy(e.target.value)}
                  className="h-9" data-testid="cart-required-by" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.1em] text-slate-500 mb-1">Notes for commercial</div>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                  rows={3} placeholder="Anything Suntronix should know…"
                  className="w-full border border-slate-200 rounded-md p-2 text-[13px] focus:outline-none focus:border-slate-500"
                  data-testid="cart-notes" />
              </div>
            </div>

            <div className="mt-5 flex gap-2">
              <Button variant="outline" onClick={onClose} className="h-10 flex-1"
                data-testid="cart-close-secondary">
                Keep shopping
              </Button>
              <Button className="flex-1 h-10 bg-slate-900 text-white hover:bg-slate-800"
                onClick={() => onRequestQuote?.({
                  delivery_country: country || null,
                  required_by: requiredBy || null,
                  notes: notes || null,
                })}
                data-testid="cart-request-quote">
                <Send className="h-4 w-4 mr-1.5" />
                Request quote
              </Button>
            </div>
          </>
        )}
    </A11yDrawer>
  );
}
